import { readonly, shallowRef } from "vue";
import {
  getLocale,
  registerTranslationLocaleReader,
  setLocale,
  subscribeToLocale,
  type Locale,
} from "./locale";
import {
  translate,
  translatePlural,
  type MessageParams,
  type PluralMessageKey,
} from "./translate";
import type { MessageKey } from "./messages/ko";

const localeRef = shallowRef<Locale>(getLocale());
let storeBridgedToRef = false;

registerTranslationLocaleReader(trackedLocale);

/**
 * store → ref 단방향 미러. 모듈당 한 번만 구독해 컴포넌트 수와 무관하게 리스너가 하나다.
 */
function bridgeStoreToRef(): void {
  if (storeBridgedToRef) return;
  storeBridgedToRef = true;
  localeRef.value = getLocale();
  subscribeToLocale((nextLocale) => {
    localeRef.value = nextLocale;
  });
}

/**
 * 현재 로케일을 반응형으로 읽는다. computed·렌더 안에서 부르면 언어 전환 때 다시 계산된다.
 */
function trackedLocale(): Locale {
  bridgeStoreToRef();
  return localeRef.value;
}

/**
 * Vue 컴포넌트용 번역 composable. 플러그인 설치 없이 `setup()` 에서 바로 쓴다 —
 * 기존 컴포넌트 테스트가 i18n 설치 없이 마운트하므로 이 함수가 유일한 진입점이어야 한다.
 */
export function useI18n() {
  bridgeStoreToRef();
  localeRef.value = getLocale();
  const t = (key: MessageKey, params?: MessageParams): string =>
    translate(localeRef.value, key, params);
  const tn = (
    baseKey: PluralMessageKey,
    count: number,
    params?: MessageParams
  ): string => translatePlural(localeRef.value, baseKey, count, params);
  return { t, tn, locale: readonly(localeRef), setLocale };
}
